import React, { useState } from 'react';
import { Form, Button, Modal, Row, Col, Card } from 'react-bootstrap';
import { Calendar, X } from 'lucide-react';

const CustomDateFilter = ({ allSales, onFilterChange }) => {
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateMode, setDateMode] = useState('single'); // 'single' ou 'range'
  const [selectedDate, setSelectedDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCustomDateActive, setIsCustomDateActive] = useState(false);

  // ✅ Calculer le CA total (exclut les annulées)
  const calculateRevenue = (sales) => {
    return sales
      .filter(sale => !sale.isCancelled) // Exclure les ventes annulées
      .reduce((sum, sale) => sum + (sale.total || 0), 0);
  };

  // ✅ Compter toutes les ventes (inclut les annulées)
  const countAllSales = (sales) => {
    return sales.length; // Inclut les ventes annulées
  };

  // ✅ Filtrer les ventes par date (garde les annulées pour le compteur)
  const filterSalesByDate = () => {
    if (!allSales || allSales.length === 0) return [];

    // On garde TOUTES les ventes (y compris annulées) pour le compteur
    let filteredSales = allSales;

    if (dateMode === 'single' && selectedDate) {
      // Filtrer pour une date spécifique
      const targetDate = new Date(selectedDate);
      targetDate.setHours(0, 0, 0, 0);
      
      return filteredSales.filter(sale => {
        const saleDate = new Date(sale.createdAt || sale.saleDate);
        saleDate.setHours(0, 0, 0, 0);
        return saleDate.getTime() === targetDate.getTime();
      });
    } else if (dateMode === 'range' && startDate && endDate) {
      // Filtrer pour une plage de dates
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Inclure toute la journée de fin

      return filteredSales.filter(sale => {
        const saleDate = new Date(sale.createdAt || sale.saleDate);
        return saleDate >= start && saleDate <= end;
      });
    }

    return filteredSales;
  };

  // Appliquer le filtre
  const handleApplyFilter = () => {
    const filteredSales = filterSalesByDate();
    const revenue = calculateRevenue(filteredSales);

    setIsCustomDateActive(true);
    setShowDateModal(false);

    // Transmettre les résultats au composant parent
    onFilterChange({
      filteredSales,
      revenue,
      dateMode,
      selectedDate: dateMode === 'single' ? selectedDate : null,
      startDate: dateMode === 'range' ? startDate : null,
      endDate: dateMode === 'range' ? endDate : null
    });
  };

  // Réinitialiser le filtre
  const handleResetFilter = () => {
    setSelectedDate('');
    setStartDate('');
    setEndDate('');
    setIsCustomDateActive(false);
    onFilterChange({
      filteredSales: null,
      revenue: null,
      dateMode: null,
      selectedDate: null,
      startDate: null,
      endDate: null
    });
  };

  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtenir le label actif
  const getActiveFilterLabel = () => {
    if (!isCustomDateActive) return null;

    if (dateMode === 'single' && selectedDate) {
      return `${formatDate(selectedDate)}`;
    } else if (dateMode === 'range' && startDate && endDate) {
      return `${formatDate(startDate).slice(0, 5)} - ${formatDate(endDate)}`;
    }
    return null;
  };

  return (
    <>
      {/* ✅ Bouton compact pour ouvrir le modal */}
      <Button
        variant={isCustomDateActive ? "primary" : "outline-primary"}
        size="sm"
        onClick={() => setShowDateModal(true)}
        className="d-flex align-items-center"
        title={isCustomDateActive ? getActiveFilterLabel() : "Choisir une date personnalisée"}
      >
        <Calendar size={16} className="me-1" />
        <span className="d-none d-sm-inline">
          {isCustomDateActive ? getActiveFilterLabel() : 'Revoir les ventes'}
        </span>
        <span className="d-sm-none">📅</span>
      </Button>

      {/* ✅ Bouton X pour réinitialiser (seulement si actif) */}
      {isCustomDateActive && (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleResetFilter}
          className="ms-1"
          title="Réinitialiser le filtre de date"
        >
          <X size={16} />
        </Button>
      )}

      {/* Modal de sélection de date */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Calendar size={24} className="me-2" />
            Filtrer par date personnalisée
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Choix du mode */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Type de filtre</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Date spécifique"
                name="dateMode"
                id="date-single"
                checked={dateMode === 'single'}
                onChange={() => setDateMode('single')}
              />
              <Form.Check
                inline
                type="radio"
                label="Plage de dates"
                name="dateMode"
                id="date-range"
                checked={dateMode === 'range'}
                onChange={() => setDateMode('range')}
              />
            </div>
          </Form.Group>

          {/* Date spécifique */}
          {dateMode === 'single' && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Choisir une date</Form.Label>
              <Form.Control
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]} // Pas de dates futures
              />
            </Form.Group>
          )}

          {/* Plage de dates */}
          {dateMode === 'range' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Date de début</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Date de fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* ✅ Aperçu des résultats */}
          {((dateMode === 'single' && selectedDate) || 
            (dateMode === 'range' && startDate && endDate)) && (
            <Card bg="light" className="mt-3 border-0">
              <Card.Body>
                <div className="text-muted small mb-2">
                  <i className="bi bi-info-circle me-1"></i>
                  Aperçu des résultats
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold">Ventes trouvées :</span>
                  <span className="badge bg-primary px-3 py-2">
                    {countAllSales(filterSalesByDate())}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Chiffre d'affaires :</span>
                  <span className="text-success fw-bold fs-6">
                    {calculateRevenue(filterSalesByDate()).toLocaleString('fr-FR')} F
                  </span>
                </div>
                <div className="text-muted small mt-1">
                  <i className="bi bi-info-circle-fill me-1"></i>
                  Le nombre inclut les ventes annulées
                </div>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDateModal(false)}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyFilter}
            disabled={
              (dateMode === 'single' && !selectedDate) ||
              (dateMode === 'range' && (!startDate || !endDate))
            }
          >
            <Calendar size={16} className="me-2" />
            Appliquer le filtre
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomDateFilter;